import React from 'react';
import { createContext } from '@utils/createContext';
import { useCallback, useMemo, useState } from 'react';
import { CatalogStorefront, HeaderMainMenus, FooterMainMenus, ValuePropCardsData } from './storefront.types';
import { useTranslation } from 'react-i18next';

export type ShopStorefrontContext = {
  currentStorefront: CatalogStorefront;
  changeStorefront: (nextStorefront: CatalogStorefront) => void;
  isBabyStorefront: boolean;
  isWeddingStorefront: boolean;
  config: {
    homePageUrl: string;
    mobileFooterNavEnabled: boolean;
    mainMenuOptions: HeaderMainMenus;
    footerMenuOptions: FooterMainMenus;
    footerMainMenuOptions: FooterMainMenus;
    valuePropCardsData: ValuePropCardsData;
  };
};

const [Provider, useShopStorefrontContext] = createContext<ShopStorefrontContext>({ name: 'ShopStorefrontProvider' });

type ShopStorefrontProviderProps = {
  page?: MarcomPageName;
  children: React.ReactNode;
};

export const ShopStorefrontProvider = (props: ShopStorefrontProviderProps) => {
  const [storefront, setStorefront] = useState<CatalogStorefront>(() => {
    // We have only wedding and baby storefronts for now, later on we could update this logic
    return props.page === 'baby-registry' ? CatalogStorefront.baby : CatalogStorefront.wedding;
  });

  const changeStorefront = useCallback((nextStorefront: CatalogStorefront) => {
    setStorefront(nextStorefront);
  }, []);

  const { t, i18n } = useTranslation('storefront');

  const CONFIG_BY_STOREFRONT = useMemo<Record<CatalogStorefront, ShopStorefrontContext['config']>>(
    () => {
      return {
        baby: {
          homePageUrl: '/baby-registry',
          mobileFooterNavEnabled: false,
          mainMenuOptions: t('mainMenuOptions.baby') as HeaderMainMenus,
          footerMenuOptions: t('footerMenuOptions.baby'),
          footerMainMenuOptions: t('footerMainMenuOptions.baby'),
          valuePropCardsData: t('valuePropCards.baby'),
        },
        wedding: {
          homePageUrl: '/',
          mobileFooterNavEnabled: true,
          mainMenuOptions: t('mainMenuOptions.wedding') as HeaderMainMenus,
          footerMenuOptions: t('footerMenuOptions.wedding'),
          footerMainMenuOptions: t('footerMainMenuOptions.wedding'),
          valuePropCardsData: t('valuePropCards.wedding'),
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  const context = useMemo<ShopStorefrontContext>(() => {
    return {
      changeStorefront,
      currentStorefront: storefront,
      config: CONFIG_BY_STOREFRONT[storefront],
      isBabyStorefront: storefront === CatalogStorefront.baby,
      isWeddingStorefront: storefront === CatalogStorefront.wedding,
    };
  }, [CONFIG_BY_STOREFRONT, changeStorefront, storefront]);
  return <Provider value={context}>{props.children}</Provider>;
};

export { useShopStorefrontContext };
